import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Youtube } from "lucide-react";
import { useApp } from "../context/AppContext";
import { subscribeNewsletter } from "../lib/api";
import { Logo } from "./Logo";
import { Marquee } from "./Marquee";
import { useSite } from "../context/SiteContext";

export const Footer = () => {
  const { t } = useApp();
  const { s, raw, list } = useSite();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await subscribeNewsletter(email);
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <footer className="border-t border-kult-border bg-kult-black" data-testid="site-footer">
      <Marquee items={list("marquee_main", ["JOIN THE CULT", "PORTO MADE", "NEW DROP", "INVICTA"])} bg="bg-kult-yellow" />

      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <Logo size={42} />
          <p className="mt-4 max-w-md text-kult-muted font-body" data-testid="footer-tag">
            {s("footer_tag_full", `${s("footer_tag", t.footer.tag)}. Apparel forged in Porto, worn worldwide.`)}
          </p>
          <div className="flex gap-4 mt-6" data-testid="footer-socials">
            {[
              { key: "instagram", url: raw("social_instagram", ""), hidden: raw("social_instagram_hidden", false) === true, Icon: Instagram, label: "Instagram" },
              { key: "twitter", url: raw("social_twitter", ""), hidden: raw("social_twitter_hidden", false) === true, Icon: Twitter, label: "Twitter" },
              { key: "youtube", url: raw("social_youtube", ""), hidden: raw("social_youtube_hidden", false) === true, Icon: Youtube, label: "YouTube" },
            ]
              .filter((x) => !x.hidden && x.url && x.url.trim())
              .map(({ key, url, Icon, label }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="border border-kult-border p-3 hover:border-kult-pink hover:text-kult-pink transition-colors"
                  data-testid={`footer-social-${key}`}
                >
                  <Icon size={16} />
                </a>
              ))}
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-mono text-xs tracking-[0.25em] text-kult-yellow mb-4">EXPLORE</h4>
          <ul className="space-y-2 font-body">
            <li><Link to="/shop" className="kult-link">{t.nav.shop}</Link></li>
            <li><Link to="/events" className="kult-link">{t.nav.events}</Link></li>
            <li><Link to="/artists" className="kult-link">{t.nav.artists}</Link></li>
            <li><Link to="/about" className="kult-link">{t.nav.about}</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="font-mono text-xs tracking-[0.25em] text-kult-yellow mb-4">
            {s("newsletter_title", t.sections.newsletter_title)}
          </h4>
          <p className="text-sm text-kult-muted mb-4">{s("newsletter_sub", t.sections.newsletter_sub)}</p>
          <form onSubmit={submit} className="flex" data-testid="newsletter-form">
            <input
              type="email"
              required
              placeholder={t.sections.newsletter_placeholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
              data-testid="newsletter-email-input"
            />
            <button
              type="submit"
              className="bg-kult-pink text-black px-5 font-mono text-xs tracking-widest hover:bg-kult-yellow transition-colors"
              data-testid="newsletter-submit"
            >
              {t.sections.newsletter_cta}
            </button>
          </form>
          {status === "success" && (
            <p className="mt-3 text-sm text-kult-yellow font-mono" data-testid="newsletter-success">
              {t.sections.newsletter_success}
            </p>
          )}
          {status === "error" && (
            <p className="mt-3 text-sm text-red-400 font-mono">Try again.</p>
          )}
        </div>
      </div>

      <div className="border-t border-kult-border py-6 text-center">
        <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted">
          {s("footer_rights", t.footer.rights)}
        </p>
      </div>
    </footer>
  );
};
